import { DecisionActivity } from "../../models/home";
import { LanguageService } from "../language/language.service";
import { HomeDecisionDetailTechnicalI18nService } from "./home-decision-detail-technical-i18n.service";

export class HomeDecisionI18nCore {
  constructor(
    private readonly lang: LanguageService,
    private readonly technicalI18n: HomeDecisionDetailTechnicalI18nService
  ) {}

  translateTechnicalValue(entryKey: string, value: unknown): string {
    return this.technicalI18n.translateTechnicalValue(entryKey, value);
  }
  isTechnicalValueMultiline(value: unknown): boolean { return this.technicalI18n.isTechnicalValueMultiline(value); }
  translateFieldKey(fieldKey: string, suffix: "label" | "placeholder" | "helper"): string | null {
    const key = this.normalizeKey(fieldKey);
    return key ? this.translateKnownKey(`home.decisionDetail.actionField.${key}.${suffix}`) : null;
  }
  translateState(state?: string | null): string | null { return this.translateByNormalizedKey(state, "home.decisionStates."); }

  translateDateEvidenceLabel(rawLabel?: string | null): string | null {
    if (!rawLabel) return null;
    const match = /^Date:\s*(.+)$/i.exec(rawLabel.trim());
    if (!match) return null;
    const field = this.technicalI18n.translateEnumerationToken(match[1], "evidence.date") ?? this.humanizeKey(match[1]);
    return this.interpolate("home.decisionDetail.evidenceLabel.dateWithField", { field }) ?? `Date: ${field}`;
  }

  translateEvidenceLabelToken(rawLabel?: string | null): string | null {
    const key = this.normalizeKey(rawLabel);
    if (!key) return null;
    if (key === "document_type") return this.translateKnownKey("home.decisionDetail.evidenceType.artifact_type");
    if (key === "operational_role") return this.translateKnownKey("home.decisionDetail.evidenceType.role_classification");
    return this.translateKnownKey(`home.decisionDetail.evidenceType.${key}`);
  }

  translateCopy(raw?: string | null): string | null {
    if (!raw) return null;
    return this.shouldTranslateResponses() ? this.translateDynamicCopy(raw) ?? raw : raw;
  }
  translateDynamicCopy(raw?: string | null): string | null {
    if (!raw || !this.shouldTranslateResponses()) return null;
    const key = this.normalizeKey(raw);
    return key ? this.translateKnownKey(`home.decisionDetail.dynamic.copy.${key}`) : null;
  }
  translateByNormalizedKey(value: string | null | undefined, prefix: string): string | null {
    const key = this.normalizeKey(value);
    return key ? this.translateKnownKey(`${prefix}${key}`) : null;
  }
  translateKnownKey(key: string): string | null {
    const translated = this.lang.translate(key);
    return translated === key ? null : translated;
  }
  interpolate(key: string, values: Record<string, string>): string | null {
    const template = this.translateKnownKey(key);
    return template?.replace(/\{(\w+)\}/g, (_match, token: string) => values[token] ?? "") ?? null;
  }
  resolveActorLabel(activity: DecisionActivity): string {
    if (activity.actor?.kind === "system") return this.lang.translate("home.decisionDetail.actor.system");
    const displayName = (activity.actor?.displayName ?? activity.actor?.userId ?? "").trim();
    return !displayName || displayName.includes("|")
      ? this.lang.translate("home.decisionDetail.actor.user")
      : displayName;
  }
  shouldTranslateResponses(): boolean { return this.lang.getCurrentLang() !== "en"; }
  normalizeKey(value?: string | null): string | null {
    const trimmed = value?.trim();
    if (!trimmed) return null;
    return trimmed.replace(/([a-z0-9])([A-Z])/g, "$1_$2").normalize("NFKD")
      .replace(/[\u0300-\u036f]/g, "").replace(/[^a-zA-Z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "").toLowerCase();
  }
  humanizeKey(value: string): string {
    return value.replace(/([a-z0-9])([A-Z])/g, "$1 $2").replace(/[_-]+/g, " ")
      .replace(/\s+/g, " ").trim()
      .replace(/(^|\s)(\w)/g, (_match, prefix: string, letter: string) => `${prefix}${letter.toUpperCase()}`);
  }
}
